/** @jsx jsx */
import { jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import defaultsDeep from 'lodash/defaultsDeep';
import { compose } from 'lodash/fp';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import truncate from 'lodash/truncate';
import {
  CLOUDINARY_BASE_VALUES,
  LOCATION_IMG,
} from '../../../../utils/constants';
import { RICH_FILTERING as dict } from '../../../../utils/dictionarykeys';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import Picture from '../../../ui/Picture';
import PriceGenerator from '../../../ui/PriceGenerator';
import { Breakpoints } from '../../../../styles/Grid/GridSizes';
import { withConsumer } from '../../../../contexts';
import { RichFilteringConsumer } from '../../../../contexts/RichFilteringContext';
import * as styles from './index.styles';

export const defaultData = {
  Location: {
    Address: '',
    Brand: '',
    Id: 0,
    ImageBrand: '',
    ImageUrl: '',
    IsFeatured: false,
    IsPartner: false,
    // Lat: '',
    // Long: '',
    lat: '',
    lng: '',
    Name: '',
    Url: '',
  },
  Solution: {
    Name: '',
    Price: 0,
    Currency: '',
    PriceUnit: '',
  },
  FallBackImage: {
    value: {
      src: LOCATION_IMG.FIRST.FALLBACK,
    },
  },
};

const imageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    height: 166,
    width: 295,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    height: 151,
    width: 269,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    height: 185,
    width: 328,
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    height: 185,
    width: 327,
  },
};

const baseBrandImageSize = {
  ...CLOUDINARY_BASE_VALUES,
  width: 95,
  aspectRatio: '16:9',
  crop: 'fit',
};

const brandImageSize = {
  [Breakpoints.xl]: baseBrandImageSize,
  [Breakpoints.lg]: baseBrandImageSize,
  [Breakpoints.md]: baseBrandImageSize,
  [Breakpoints.sm]: baseBrandImageSize,
};

const renderSolutionItem = solution => (
  <div css={styles.solutionsItemsWrapper}>
    <span css={styles.solutionItemName}>
      <Text field={{ value: solution.Name }} editable={false} />
    </span>
    <span css={styles.solutionItemPrice}>
      <PriceGenerator price={solution} type="listing" />
    </span>
  </div>
);

const prepareSrc = (image, isBrandImage = false) => {
  if (image.value) {
    image.value.src = isBrandImage
      ? `/${get(image, 'value.src', '')}`
      : get(image, 'value.src', '');
  }
  return image;
};

export class Card extends SafePureComponent {
  state = {
    fields: defaultsDeep(this.props.fields, defaultData),
  };

  componentDidUpdate(prevProps) {
    if (isEqual(prevProps.fields, this.props.fields)) return;

    this.setState({
      fields: defaultsDeep(this.props.fields, prevProps.fields),
    });
  }

  safeRender() {
    const { t } = this.props;
    const { Location, Solution, FallBackImage } = this.state.fields;

    const { onHoverCard } = get(this.props, 'richFilteringContext.actions');

    const {
      Address,
      Id,
      ImageBrand,
      ImageUrl,
      IsFeatured,
      IsPartner,
      Name,
      Url,
    } = Location;

    let parsedImage = !isEmpty(ImageUrl) && JSON.parse(ImageUrl);
    let parsedBrandImage = !isEmpty(ImageBrand) && JSON.parse(ImageBrand);

    parsedImage = prepareSrc(parsedImage);
    parsedBrandImage = parsedBrandImage && prepareSrc(parsedBrandImage, true);

    return (
      <a
        data-item="result-card"
        href={Url}
        rel="noopener noreferrer"
        css={styles.card}
        data-id={Id}
        onMouseOver={onHoverCard}
        onMouseOut={onHoverCard}
        onFocus={onHoverCard}
        onBlur={onHoverCard}
      >
        {IsFeatured && (
          <span css={styles.isFeaturedTag}>{t(dict.FEATURED)}</span>
        )}
        {!!get(parsedImage, 'value') && (
          <div css={styles.pictureWrapper}>
            <Picture
              image={parsedImage}
              imageCss={styles.image}
              pictureCss={styles.picture}
              imageSizes={imageSizes}
              fallbackAlt={`${t(LOCATION_IMG.FIRST.DICTIONARY_KEY)} ${Name}`}
              isClientLink
              fallBack={FallBackImage}
            />
          </div>
        )}
        <div className="result-cards-info" css={styles.cardInfo}>
          <div css={styles.titleAddress}>
            <h2 css={styles.cardTitle}>{truncate(Name, { length: 38 })}</h2>
            <p css={styles.cardAddress}>{truncate(Address, { length: 88 })}</p>
          </div>

          {Solution && renderSolutionItem(Solution)}

          {!!get(parsedBrandImage, 'value') && (
            <div css={styles.footerData}>
              <Picture
                image={parsedBrandImage}
                imageCss={styles.imageBrand}
                imageSizes={brandImageSize}
              />
              {IsPartner && (
                <span css={styles.partnerTag}>
                  {t(dict.ASSOCIATED_PARTNER)}
                </span>
              )}
            </div>
          )}
        </div>
      </a>
    );
  }
}

Card.displayName = 'Rich Card';

Card.defaultProps = {
  fields: defaultData,
  t: key => `dict-${key}`,
};

Card.propTypes = {
  Location: PropTypes.shape({
    Address: PropTypes.string,
    Brand: PropTypes.string,
    Id: PropTypes.string,
    ImageBrand: PropTypes.string,
    ImageUrl: PropTypes.string,
    IsFeatured: PropTypes.bool,
    IsPartner: PropTypes.bool,
    // Lat: PropTypes.string,
    // Long: PropTypes.string,
    Name: PropTypes.string,
    Url: PropTypes.string,
    lat: PropTypes.string,
    lng: PropTypes.string,
  }),
  Solution: PropTypes.shape({
    Name: PropTypes.string,
    Price: PropTypes.number,
    Currency: PropTypes.string,
    PriceUnit: PropTypes.string,
  }),
  FallBackImage: PropTypes.object,
};

export default compose(
  withConsumer(RichFilteringConsumer, 'richFilteringContext'),
  withNamespaces(),
)(Card);
